import { useAuth } from "../../../context/AuthContext";
import React from "react";
import { useState, useRef, useEffect } from "react";
import "./dashboard.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Button from "../../../components/Button"
import Breadcrumbs from "../../../components/Breadcrumbs";
import api from "../../../api/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ScatterChart,
  Scatter,
  ZAxis,
  ComposedChart,
  Line,
  CartesianGrid,
  LineChart,
  Sankey,
} from "recharts";
  
const Intelligence = () => {
  const { user } = useAuth();
  
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [open, setOpen] = useState(false);
  const [funnelData, setFunnelData] = useState([]);
  const [stageConversion, setStageConversion] = useState([]);
  const [quoteEffectiveness, setQuoteEffectiveness] = useState({ data: [], summary: {} });
  const [fclLclData, setFclLclData] = useState({ data: [], summary: {} });
  const [blGenerationData, setBlGenerationData] = useState({ data: [], summary: {} });
   
  const inputRef = useRef(null);
  
  const fetchFunnelConversion = async (start, end) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const params = {};
      if (start && end) {
        params.fromDate = start.toISOString();
        params.toDate = end.toISOString();
      }

      console.log("Fetching funnel conversion with params:", params);

      const { data } = await api.get("/dashboard/funnel-conversion", {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      console.log("Funnel conversion response:", data);
      setFunnelData(data.data || []);
      setStageConversion(data.stageConversion || []);
    } catch (err) {
      console.error("Failed to fetch funnel data:", err.response || err);
    }
  };

  const fetchQuoteEffectiveness = async (start, end) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const params = {};
      if (start && end) {
        params.fromDate = start.toISOString();
        params.toDate = end.toISOString();
      }

      const { data } = await api.get("/dashboard/quote-effectiveness", {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setQuoteEffectiveness({ data: data.data || [], summary: data.summary || {} });
    } catch (err) {
      console.error("Failed to fetch quote effectiveness:", err.response || err);
    }
  };

  const fetchFclLclSplit = async (start, end) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const params = {};
      if (start && end) {
        params.fromDate = start.toISOString();
        params.toDate = end.toISOString();
      }

      const { data } = await api.get("/dashboard/fcl-lcl-split", {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
    setFclLclData({ data: data.data || [], summary: data.summary || {} });
    } catch (err) {
    console.error("Failed to fetch FCL/LCL split:", err.response || err);
    }
  };

  const fetchBlGenerationTrend = async (start, end) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const params = {};
      if (start && end) {
        params.fromDate = start.toISOString();
        params.toDate = end.toISOString();
      }

      const { data } = await api.get("/dashboard/bl-generation-trend", {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setBlGenerationData({ data: data.data || [], summary: data.summary || {} });
    } catch (err) {
      console.error("Failed to fetch BL generation trend:", err.response || err);
    }
  };
   
  useEffect(() => {
    fetchFunnelConversion();
    fetchQuoteEffectiveness();
    fetchFclLclSplit();
    fetchBlGenerationTrend();
  }, [user]);

  const handleFilter = () => {
    if (startDate && endDate) {
      fetchFunnelConversion(startDate, endDate);
      fetchQuoteEffectiveness(startDate, endDate);
      fetchFclLclSplit(startDate, endDate);
      fetchBlGenerationTrend(startDate, endDate);
      setOpen(false);
    }
  };

  const handleClearFilter = () => {
    setDateRange([null, null]);
    setOpen(false);
    fetchFunnelConversion();
    fetchQuoteEffectiveness();
    fetchFclLclSplit();
    fetchBlGenerationTrend();
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  if (!user) return <div>Loading...</div>;

  return (
    <>
      <Breadcrumbs />
      {/* Date Filter */}
      <div className="row align-items-center mb-4">
        <div className="col-sm-6 col-md-3">
          <input
            ref={inputRef}
            readOnly
            value={
              startDate && endDate
                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                : ""
            }
            onClick={() => setOpen(!open)}
            placeholder="Select Date Range"
            className="form-control"
          />
          {open && (
            <div className="position-absolute bg-white shadow p-3 rounded mt-1 z-3">
              <DatePicker
                inline
                selectsRange
                monthsShown={2}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => setDateRange(update)}
                maxDate={new Date()}
              />
              <div className="d-flex justify-content-end mt-2">
                <Button onClick={handleToggle} text="Done" btncls="me-2" />
                <Button
                  onClick={handleClearFilter}
                  text="Clear"
                  variant="danger"
                />
              </div>
            </div>
          )}
        </div>
        <div className="col-sm-6 col-md-4 col-lg-5 col-xl-3 d-flex mt-3 mt-sm-0">
          <Button
            onClick={handleFilter}
            text="Apply Filter"
            btncls="me-2 mt-0 w-100 w-sm-auto"
          />
          <Button
            onClick={handleClearFilter}
            text="Clear Filter"
            variant="danger"
            btncls="mt-0 w-100 w-sm-auto light-red"
          />
        </div>
      </div>

      {/* Funnel Conversion Chart */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="mainbox">
            <h2 className="mb-3" style={{ fontSize: "1.1rem", fontWeight: "700" }}>Funnel Conversion</h2>
            {funnelData.length > 0 ? (
              <>
                <div className="funnel-container" style={{ padding: "20px 40px" }}>
                  {funnelData.map((item, index) => {
                    const maxCount = funnelData[0]?.count || 1;
                    const widthPercent = item.count > 0 
                      ? (item.count / maxCount) * 100
                      : 0;
                    const getMinWidth = () => {
                      if (index === funnelData.length - 2) return "10%";
                      if (index === funnelData.length - 1) return "8%";
                      return "15%";
                    };
                    return (
                      <div key={index} style={{ marginBottom: 0 }}>
                        <div 
                          className="funnel-bar d-flex align-items-center justify-content-between px-3 position-relative"
                          style={{
                            width: `${widthPercent}%`,
                            minWidth: getMinWidth(),
                            height: "45px",
                            backgroundColor: ["#4CAF50", "#2196F3", "#FF9800", "#9C27B0", "#E91E63", "#00BCD4", "#FF5722"][index % 7],
                            borderRadius: "10px",
                            color: "#fff",
                            margin: "0 auto",
                            cursor: "pointer",
                          }}
                        >
                          <span style={{ fontWeight: "600", fontSize: "14px" }}>{item.stage}</span>
                          <span style={{ fontWeight: "700", fontSize: "14px" }}>{item.count}</span>
                          <div 
                            className="position-absolute funnel-tooltip"
                            style={{
                              bottom: "100%",
                              left: "50%",
                              transform: "translateX(-50%)",
                              backgroundColor: "#333",
                              color: "#fff",
                              padding: "6px 12px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              whiteSpace: "nowrap",
                              opacity: 0,
                              visibility: "hidden",
                              transition: "all 0.2s ease",
                              zIndex: 10,
                              marginBottom: "5px",
                              minWidth: "180px",
                            }}
                          >
                            {index === 0 
                              ? `Conversion: ${item.percentage}%` 
                              : (
                                <>
                                  {funnelData[index - 1].stage} → {item.stage}: {stageConversion[index - 1]?.rate || 0}%
                                  {(stageConversion[index - 1]?.avgHours !== undefined && stageConversion[index - 1]?.avgHours !== null) && (
                                    <div style={{ fontSize: "11px", color: "#aaa", marginTop: "2px" }}>
                                      Avg: {stageConversion[index - 1].avgHours < 24 
                                        ? `${stageConversion[index - 1].avgHours} hours` 
                                        : `${Math.round(stageConversion[index - 1].avgHours / 24)} days`}
                                    </div>
                                  )}
                                </>
                              )
                            }
                            {item.stage === "Enquiry" && item.sourceBreakdown && (
                              <div style={{ fontSize: "11px", color: "#aaa", marginTop: "4px", borderTop: "1px solid #555", paddingTop: "4px" }}>
                                Sales App: {item.sourceBreakdown.salesApp}<br />
                                NaavVaayu: {item.sourceBreakdown.naavVaayu}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="d-flex flex-wrap gap-3 mt-4 justify-content-center">
                  {funnelData.map((item, index) => (
                    <div key={index} className="d-flex align-items-center gap-2">
                      <span
                        style={{
                          backgroundColor: ["#4CAF50", "#2196F3", "#FF9800", "#9C27B0", "#E91E63", "#00BCD4"][index % 6],
                          width: "12px",
                          height: "12px",
                          borderRadius: "2px",
                        }}
                      ></span>
                      <span style={{ fontSize: "13px" }}>
                        {item.stage}: <strong>{item.count}</strong> ({item.percentage}%)
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                <p className="text-muted">No funnel data available. Please ensure backend API is running.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quote Effectiveness Matrix */}
      {quoteEffectiveness.data.length > 0 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="mainbox">
              <h2 className="mb-3" style={{ fontSize: "1.1rem", fontWeight: "700" }}>
                Quote Effectiveness Matrix
              </h2>

              <div className="row mb-4">
                <div className="col-md-4">
                  <div className="text-center p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                    <div style={{ fontSize: "14px", color: "#6c757d" }}>Total Quotes Sent</div>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#FF9800" }}>
                      {quoteEffectiveness.summary.totalQuotes}
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="text-center p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                    <div style={{ fontSize: "14px", color: "#6c757d" }}>Total Bookings Confirmed</div>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#4CAF50" }}>
                      {quoteEffectiveness.summary.totalBookings}
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="text-center p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                    <div style={{ fontSize: "14px", color: "#6c757d" }}>Overall Conversion Rate</div>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#2196F3" }}>
                      {quoteEffectiveness.summary.overallRate}%
                    </div>
                  </div>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={350}>
                <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 20 }}>
                  <XAxis
                    type="number"
                    dataKey="quotesSent"
                    name="Quotes Sent"
                    tick={{ fontSize: 12 }}
                    label={{ value: "Quotes Sent", position: "bottom", offset: 10 }}
                  />
                  <YAxis
                    type="number"
                    dataKey="bookingsConfirmed"
                    name="Bookings"
                    tick={{ fontSize: 12 }}
                    label={{ value: "Bookings Confirmed", angle: -90, position: "insideLeft" }}
                  />
                  <Tooltip 
                    content={({ payload }) => {
                      if (!payload || !payload[0]) return null;
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white shadow p-2 rounded" style={{ border: "1px solid #ddd", fontSize: "12px" }}>
                          <div style={{ fontWeight: "600", marginBottom: "4px" }}>{data.month}</div>
                          <div>Quotes Sent: <strong>{data.quotesSent}</strong></div>
                          <div>Bookings Confirmed: <strong>{data.bookingsConfirmed}</strong></div>
                          <div>Conversion: <strong>{data.conversionRate}%</strong></div>
                        </div>
                      );
                    }}
                  />
                  <Scatter name="Months" data={quoteEffectiveness.data} fill="#4CAF50">
                    {quoteEffectiveness.data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.conversionRate >= 50
                            ? "#4CAF50"
                            : entry.conversionRate >= 25
                            ? "#FF9800"
                            : "#E91E63"
                        }
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>

              <div className="d-flex flex-wrap gap-2 mt-2 justify-content-center">
                {quoteEffectiveness.data.map((item, index) => (
                  <div
                    key={index}
                    className="px-2 py-1"
                    style={{
                      fontSize: "11px",
                      backgroundColor:
                        item.conversionRate >= 50
                          ? "#e8f5e9"
                          : item.conversionRate >= 25
                          ? "#fff3e0"
                          : "#ffebee",
                      borderRadius: "4px",
                      border: `1px solid ${
                        item.conversionRate >= 50
                          ? "#4CAF50"
                          : item.conversionRate >= 25
                          ? "#FF9800"
                          : "#E91E63"
                      }`,
                    }}
                  >
                    {item.month.split(" ")[0].substring(0, 3)}: {item.quotesSent}/{item.bookingsConfirmed}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FCL vs LCL Split */}
      {fclLclData.data.length > 0 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="mainbox">
              <h2 className="mb-3" style={{ fontSize: "1.1rem", fontWeight: "700" }}>FCL vs LCL Split</h2>
              
              <div className="row mb-4">
                <div className="col-md-3">
                  <div className="text-center p-3" style={{ backgroundColor: "#e8f5e9", borderRadius: "8px" }}>
                    <div style={{ fontSize: "14px", color: "#6c757d" }}>FCL Import</div>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#4CAF50" }}>{fclLclData.summary.totalFclImport || 0}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3" style={{ backgroundColor: "#c8e6c9", borderRadius: "8px" }}>
                    <div style={{ fontSize: "14px", color: "#6c757d" }}>FCL Export</div>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#388E3C" }}>{fclLclData.summary.totalFclExport || 0}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3" style={{ backgroundColor: "#e3f2fd", borderRadius: "8px" }}>
                    <div style={{ fontSize: "14px", color: "#6c757d" }}>LCL Import (CBM)</div>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#2196F3" }}>{fclLclData.summary.totalLclImport?.toFixed(2) || 0}</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3" style={{ backgroundColor: "#bbdefb", borderRadius: "8px" }}>
                    <div style={{ fontSize: "14px", color: "#6c757d" }}>LCL Export (CBM)</div>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#1976D2" }}>{fclLclData.summary.totalLclExport?.toFixed(2) || 0}</div>
                  </div>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={fclLclData.data} margin={{ top: 20, right: 30, bottom: 40, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Month', position: 'bottom', offset: 10 }}
                  />
                  <YAxis 
                    yAxisId="left" 
                    tick={{ fontSize: 12 }}
                    label={{ value: 'FCL Containers', angle: -90, position: 'insideLeft' }}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    tick={{ fontSize: 12 }}
                    label={{ value: 'LCL CBM', angle: 90, position: 'insideRight' }}
                  />
                  <Tooltip />
                  <Bar yAxisId="left" dataKey="fclImport" name="FCL Import" fill="#4CAF50" barSize={40} stackId="fcl" />
                  <Bar yAxisId="left" dataKey="fclExport" name="FCL Export" fill="#388E3C" barSize={40} stackId="fcl" />
                  <Line 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="lclImport" 
                    name="LCL Import" 
                    stroke="#2196F3" 
                    strokeWidth={2}
                    dot={{ fill: "#2196F3", strokeWidth: 2 }}
                  />
                  <Line 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="lclExport" 
                    name="LCL Export" 
                    stroke="#1976D2" 
                    strokeWidth={2}
                    dot={{ fill: "#1976D2", strokeWidth: 2 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* BL Generation Trend */}
      {blGenerationData.data.length > 0 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="mainbox">
              <h2 className="mb-3" style={{ fontSize: "1.1rem", fontWeight: "700" }}>BL Generation Trend</h2>
              
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="text-center p-3" style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                    <div style={{ fontSize: "14px", color: "#6c757d" }}>Total BL Generated</div>
                    <div style={{ fontSize: "24px", fontWeight: "700", color: "#9C27B0" }}>{blGenerationData.summary.totalBL || 0}</div>
                  </div>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={blGenerationData.data} margin={{ top: 20, right: 30, bottom: 40, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value ? new Date(value).toLocaleDateString('en-IN') : ''}
                    label={{ value: 'Date', position: 'bottom', offset: 10 }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    label={{ value: 'BL Count', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    labelFormatter={(value) => value ? new Date(value).toLocaleDateString('en-IN') : ''}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="blCount" 
                    name="BL Generated" 
                    stroke="#9C27B0" 
                    strokeWidth={2}
                    dot={{ fill: "#9C27B0", strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Shipment Lifecycle Tracker - Sankey Diagram */}
      {funnelData.length > 0 && funnelData.length >= 3 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="mainbox">
              <h2 className="mb-3" style={{ fontSize: "1.1rem", fontWeight: "700" }}>Shipment Lifecycle Tracker</h2>
              
              {(() => {
                const stages = funnelData.slice(2);
                const totalEnquiries = funnelData[0]?.count || 1;
                const dropOffRate = ((totalEnquiries - stages[stages.length - 1].count) / totalEnquiries * 100).toFixed(1);
                const stageColors = ["#2E7D32", "#1565C0", "#EF6C00", "#7B1FA2", "#C62828"];
                
                const nodeWidth = 40;
                const nodeSpacing = 200;
                const svgWidth = stages.length * nodeSpacing + 40;
                const svgHeight = 380;
                const nodeStartX = 20;
                const nodeY = 40;
                const minNodeHeight = 50;
                const nodeHeight = svgHeight - nodeY * 2;
                
                const getNodeHeight = (count) => Math.max(minNodeHeight, (count / totalEnquiries) * (nodeHeight - minNodeHeight));
                
                const nodes = stages.map((stage, i) => ({
                  ...stage,
                  x: nodeStartX + i * nodeSpacing,
                  width: nodeWidth,
                  height: getNodeHeight(stage.count),
                  y: nodeY + (nodeHeight - getNodeHeight(stage.count)) / 2,
                  color: stageColors[i % stageColors.length],
                }));
                
                return (
                  <>
                    <div className="row mb-4">
                      <div className="col-md-4">
                        <div className="text-center p-3" style={{ backgroundColor: "#e8f5e9", borderRadius: "8px" }}>
                          <div style={{ fontSize: "14px", color: "#6c757d" }}>Total Enquiries</div>
                          <div style={{ fontSize: "24px", fontWeight: "700", color: "#2E7D32" }}>{totalEnquiries}</div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="text-center p-3" style={{ backgroundColor: "#ffebee", borderRadius: "8px" }}>
                          <div style={{ fontSize: "14px", color: "#6c757d" }}>Completed Shipments</div>
                          <div style={{ fontSize: "24px", fontWeight: "700", color: "#C62828" }}>{stages[stages.length - 1].count}</div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="text-center p-3" style={{ backgroundColor: "#fff3e0", borderRadius: "8px" }}>
                          <div style={{ fontSize: "14px", color: "#6c757d" }}>Drop-off Rate</div>
                          <div style={{ fontSize: "24px", fontWeight: "700", color: "#EF6C00" }}>{dropOffRate}</div>
                        </div>
                      </div>
                    </div>
                    
                    <svg width="100%" height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ display: 'block', maxWidth: '100%' }}>
                      <defs>
                        {nodes.slice(0, -1).map((node, i) => {
                          const nextNode = nodes[i + 1];
                          return (
                            <linearGradient key={`link-grad-${i}`} id={`link-grad-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor={node.color} stopOpacity="0.5" />
                              <stop offset="100%" stopColor={nextNode.color} stopOpacity="0.5" />
                            </linearGradient>
                          );
                        })}
                      </defs>
                      
                      {nodes.map((node, i) => {
                        const nextNode = nodes[i + 1];
                        if (!nextNode) return null;
                        
                        const linkStartY = node.y;
                        const linkEndY = nextNode.y;
                        
                        const midX = (node.x + nodeWidth + nextNode.x) / 2;
                        
                        return (
                          <g key={`link-${i}`}>
                            <path
                              d={`M ${node.x + nodeWidth} ${linkStartY} 
                                  C ${midX} ${linkStartY} ${midX} ${linkEndY} ${nextNode.x} ${linkEndY}
                                  L ${nextNode.x} ${linkEndY + nextNode.height}
                                  C ${midX} ${linkEndY + nextNode.height} ${midX} ${linkStartY + node.height} ${node.x + nodeWidth} ${linkStartY + node.height}
                                  Z`}
                              fill={`url(#link-grad-${i})`}
                              stroke="none"
                            />
                          </g>
                        );
                      })}
                      
                      {nodes.map((node, i) => {
                        const avgTime = stageConversion[i + 2]?.avgHours;
                        const avgTimeText = avgTime !== undefined && avgTime !== null && avgTime > 0
                          ? (avgTime < 24 ? `${avgTime}h` : `${Math.round(avgTime / 24)}d`)
                          : null;
                        
                        return (
                          <g key={`node-${i}`}>
                            <rect
                              x={node.x}
                              y={node.y}
                              width={node.width}
                              height={node.height}
                              fill={node.color}
                              rx="6"
                              ry="6"
                            />
                            <text
                              x={node.x + node.width / 2}
                              y={node.y + node.height / 2 - (avgTimeText ? 6 : 0)}
                              textAnchor="middle"
                              fill="#fff"
                              fontSize="16"
                              fontWeight="bold"
                            >
                              {node.count}
                            </text>
                            <text
                              x={node.x + node.width / 2}
                              y={node.y + node.height / 2 + 12}
                              textAnchor="middle"
                              fill="#fff"
                              fontSize="10"
                            >
                              {node.stage}
                            </text>
                            {avgTimeText && (
                              <text
                                x={node.x + node.width / 2}
                                y={node.y + node.height / 2 + 26}
                                textAnchor="middle"
                                fill="rgba(255,255,255,0.8)"
                                fontSize="9"
                              >
                                Avg: {avgTimeText}
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                    
                    <div className="d-flex flex-wrap gap-4 mt-4 justify-content-center border-top pt-3">
                      {stages.map((item, index) => {
                        const avgTime = stageConversion[index + 2]?.avgHours;
                        const avgTimeText = avgTime !== undefined && avgTime !== null && avgTime > 0
                          ? (avgTime < 24 ? `${avgTime}h` : `${Math.round(avgTime / 24)}d`)
                          : null;
                        
                        return (
                          <div key={index} className="text-center">
                            <div 
                              className="d-inline-block"
                              style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: stageColors[index % stageColors.length],
                              }}
                            />
                            <div style={{ fontSize: "12px", marginTop: "4px" }}>
                              <strong>{item.stage}</strong>
                              <br />
                              <span style={{ color: "#666" }}>{item.count}</span>
                              {avgTimeText && (
                                <span style={{ color: "#888", fontSize: "10px" }}> | {avgTimeText}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default Intelligence;